import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import App from "./App";
import "./index.css";
import "./styles/qa-rail-contrast.css";
import { installGlobalInputGuard } from "./lib/global-input-guard";
import { supabase } from "./lib/session";

// P0-3: global crash guard for huge text inputs. Caps input length
// before downstream onChange handlers run, so a 50,000-char paste in
// any field cannot trigger a re-render storm or regex backtrack.
installGlobalInputGuard();

// ─── Sentry ────────────────────────────────────────────────────────────────
// Only initialises when VITE_SENTRY_DSN is set in the build environment.
// Bryan: add VITE_SENTRY_DSN to Netlify env vars and trigger a redeploy.
declare const __APP_VERSION__: string;
const SENTRY_DSN = (import.meta as any).env?.VITE_SENTRY_DSN as string | undefined;

if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.PROD ? "production" : "development",
    release: typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "unknown",
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    // Filter low-signal noise. Supabase 401s during token refresh, browser
    // extension errors, and ResizeObserver warnings are not real signal.
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      "Non-Error promise rejection captured",
      /^Network request failed$/,
      /Failed to fetch dynamically imported module/,
    ],
    denyUrls: [
      /extensions\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
    ],
    beforeSend(event, hint) {
      // Drop Supabase 401s — these are normal during silent token refresh.
      const msg =
        (hint?.originalException as any)?.message ||
        event.message ||
        "";
      if (typeof msg === "string" && /401/.test(msg) && /supabase/i.test(msg)) {
        return null;
      }
      return event;
    },
  });

  // Forward our app's save-error CustomEvents straight to Sentry.
  window.addEventListener("aip:answer-save-error", (e: Event) => {
    const detail = (e as CustomEvent).detail || {};
    Sentry.captureException(
      new Error(
        `[answer-save-error] account=${detail.accountId} q=${detail.questionKey} — ${detail.error}`,
      ),
      { extra: detail, tags: { surface: "helix-saveAnswer" } },
    );
  });

  // Optional success breadcrumb stream — useful context when a later error fires.
  window.addEventListener("aip:answer-saved-db", (e: Event) => {
    const detail = (e as CustomEvent).detail || {};
    Sentry.addBreadcrumb({
      category: "save",
      type: "info",
      level: "info",
      message: "account_answers upsert ok",
      data: detail,
    });
  });
}

// D-058: Eagerly initialize the Supabase client singleton BEFORE React mounts.
// The GoTrueClient reads window.location.href during initialization (detectSessionInUrl)
// to extract access_token / type=recovery from the URL hash. If initialization is
// deferred until a useEffect (the previous behavior), the usePasswordRecoveryRoute
// hook fires first and rewrites the hash from
//   #access_token=...&type=recovery   →   #/login
// causing Supabase to see no tokens and never emit PASSWORD_RECOVERY, leaving the
// user stranded on the normal sign-in form after clicking a password reset link.
//
// Calling supabase() here — synchronously, before createRoot — forces the singleton
// to be created with the original URL intact, so detectSessionInUrl captures the
// recovery tokens and fires PASSWORD_RECOVERY after React mounts.
void supabase();

if (!window.location.hash) {
  window.location.hash = "#/";
}

function SentryFallback() {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "sans-serif",
        background: "#FAFAF7",
        color: "#0B0B0F",
        gap: "16px",
        padding: "24px",
        textAlign: "center",
      }}
    >
      <div style={{ fontSize: "20px", fontWeight: 700 }}>Something broke.</div>
      <div style={{ fontSize: "14px", color: "#6B6B70" }}>
        Bryan has been notified. Reload to try again.
      </div>
      <button
        onClick={() => window.location.reload()}
        style={{
          marginTop: "8px",
          padding: "10px 24px",
          borderRadius: "8px",
          background: "#0B0B0F",
          color: "#fff",
          border: "none",
          cursor: "pointer",
          fontSize: "14px",
          fontWeight: 600,
        }}
      >
        Reload
      </button>
    </div>
  );
}

createRoot(document.getElementById("root")!).render(
  <Sentry.ErrorBoundary fallback={<SentryFallback />}>
    <App />
  </Sentry.ErrorBoundary>
);
